import React, { useEffect, useState,useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, CircularProgress, Stack, SvgIcon, Button, Typography } from '@mui/material';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as ExcelIcon } from '../../assests/images/excelIcon.svg';
import { useOkta } from '../../hooks/oktaHook';
import { useSelector } from 'react-redux';
import { generateUniqueId } from '../../utils/Utils';
import { Modal, ProgressBar } from 'pepsico-ds';

const modalContainerStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '40vw',
  maxWidth: '580px',
  height: 'auto'
};

const UploadModal = ({ open, handleClose, onUploadFile,timeoutNo,setTimeoutNo, mode = 'single', ...rest }) => {
  const { userDetails } = useOkta();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const timeoutRef = useRef(null); // clear handleClose timeout
  const intervalRef = useRef(null); // clear countdown timeout
  let flow = '';

  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);

  const isBrowseDisabled = loading || uploading || (mode === 'single' && uploadedFiles.length >= 1);
  const chunkSize = 6 * 1024 * 1024; //6mb

  const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      // Handle file rejection here if needed
      setUploadMessage({
        type: 'error',
        message: 'Invalid file type or size.'
      });
      return;
    }
    handleFileUpload(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: mode === 'multiple',
    maxFiles: mode === 'single' ? 1 : undefined,
    disabled: isBrowseDisabled
  });

  const handleFileUpload = (files) => {
    if (isBrowseDisabled) {
      return;
    }

    setUploadMessage(null);
    setLoading(true);
    setCurrentFile(files[0]);

    // Simulate file loading with a timeout for demo purposes
    setTimeout(() => {
      setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
      setLoading(false);
    }, 1000);
  };

  const handleDeleteFile = (fileName) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.name !== fileName));
  };

  const handleUploadClick = async () => {
    if (uploadedFiles.length === 0 && loading) {
      return;
    }
    setUploading(true);

    try {
      setUploadMessage(null);
      setUploadProgress(0);
      const { filterObj, pageId, currentActiveCycleID, userLevel, userType, regionOption } = rest;
      const fileObj = uploadedFiles[0];
      const filesize = fileObj?.size;
      console.log('TCL: handleUploadClick -> filesize', filesize);
      if (pageId === 'Voting') {
        flow = 'BulkVoting';
      } else if (pageId === 'BulkUpload') {
        flow = 'BulkUpload';
      }
      const chunkThreshold = 9 * 1024 * 1024; //9mb
      if (pageId === 'manualUpload' && filesize > chunkThreshold) {
        for (const file of uploadedFiles) {
          const uniqueId = generateUniqueId();
          const totalChunks = Math.ceil(file.size / chunkSize);

          for (let chunkIndex = 1; chunkIndex <= totalChunks; chunkIndex++) {
            const start = (chunkIndex - 1) * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const chunk = file.slice(start, end);
            const formData = new FormData();
            formData.append('uniqueId', uniqueId);
            formData.append('originalFileName', file.name);
            formData.append('file', chunk);
            formData.append('fileType', filterObj?.dataType);
            formData.append('market', filterObj?.market ?? selectedBu);
            formData.append('userName', userDetails?.email);
            formData.append('chunkIndex', chunkIndex);
            formData.append('totalChunks', totalChunks);
            formData.append('version', 'v2');
            formData.append('flow', flow);

            const response = await onUploadFile(formData, (progressEvent) => {
              const chunkProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const totalProgress = Math.round(
                ((chunkIndex - 1) * 100 + chunkProgress) / totalChunks
              );
              setUploadProgress(totalProgress);
            });
            if (chunkIndex === totalChunks) {
              setUploadMessage({
                type: 'success',
                message: response?.message
              });
            }
          }
        }
      } else {
        // Original implementation for other page types
        const formData = new FormData();
        uploadedFiles.forEach((file) => {
          formData.append('originalFileName', file?.name);
          formData.append('file', file);
          formData.append('fileType', filterObj?.dataType);
          formData.append('market', filterObj?.market ?? selectedBu);
          formData.append('userName', userDetails.email);
          formData.append('votingLevel', userLevel);
          formData.append('voteOwnerFunctionName', userType);
          formData.append('cycleId', currentActiveCycleID);
          formData.append('regionName', regionOption);
          formData.append('flow', flow);
        });

        const response = await onUploadFile(formData, (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        });

        setUploadMessage({
          type: 'success',
          message: response?.message
        });
      }

      setUploadedFiles([]);
      setUploading(false);
    } catch (error) {
      setUploadMessage({ type: 'error', message: error?.message });
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setUploadedFiles([]);
    }
    setUploadMessage(null);
  }, [open]);

  useEffect(() => {
    if (uploadMessage?.type === 'success') {
      // countdown timer
      if (timeoutNo > 0) {
        intervalRef.current = setTimeout(() => {
          setTimeoutNo(timeoutNo - 1);
        }, 1000);
      }

      // auto-close timer
      timeoutRef.current = setTimeout(() => {
        handleClose();
        setTimeoutNo(10);
      }, 10000);
    }

    // cleanup
    return () => {
      clearTimeout(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [uploadMessage, timeoutNo]);

  return (
    <Modal
      data-testid="upload-modal"
      showModal={open}
      closeOnOutsideClick
      onCloseModal={handleClose}
      type="S"
      // onClose={handleClose}
      aria-labelledby="Upload File"
      aria-describedby="Excel file upload"
      showTertiaryButton={false}
      showPrimaryButton={false}
      showSecondaryButton={false}
      showAuxiliaryButton={false}
      showLink={false}
      style={{
        height: '400px',
        width: '350px'
      }}>
      <Box sx={modalContainerStyle}>
        <Box sx={{ textAlign: 'end' }}>
          <CloseIcon
          data-testid="close-modal-button"
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: '#FFFFFF',
              fontSize: '22px',
              cursor: 'pointer'
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}>
          <Typography variant="h5" sx={{ color: '#3A3A3A' }}>
            Upload File
          </Typography>

          {uploadMessage && (
            <Stack
              data-testid="upload-message"
              direction="row"
              alignItems="center"
              gap="8px"
              sx={{
                backgroundColor: uploadMessage.type === 'error' ? '#FBE6E9' : '#CCE7DD',
                padding: '8px 16px',
                borderRadius: '4px',
                width: '100%'
              }}>
              {uploadMessage.type === 'error' ? (
                <ErrorOutlineOutlinedIcon
                  sx={{
                    color: '#D40020',
                    fontSize: '20px'
                  }}
                />
              ) : (
                <CheckCircleOutlinedIcon
                  sx={{
                    color: '#008553',
                    fontSize: '20px'
                  }}
                />
              )}
              <Typography
                variant="small"
                sx={{
                  color: '#000000'
                }}>
                {uploadMessage.type === 'success'
                  ? `${uploadMessage.message}. Closing this window in ${timeoutNo} seconds`
                  : uploadMessage.message}
              </Typography>
            </Stack>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              width: '100%'
            }}>
            <Box
              {...getRootProps()}
              data-testid="file-dropzone"
              sx={{
                width: '100%',
                height: '160px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isBrowseDisabled ? 'not-allowed' : 'pointer',
                border: `1px dashed ${
                  mode === 'single' && uploadedFiles.length >= 1
                    ? '#D0D0D0'
                    : isDragReject
                      ? '#D40020'
                      : isDragActive
                        ? '#CBDDF6'
                        : '#D7E5F8'
                }`,
                borderRadius: '8px',
                backgroundColor:
                  mode === 'single' && uploadedFiles.length >= 1
                    ? '#D0D0D0'
                    : isDragReject
                      ? '#FBE6E9' // Light blue when dragging over
                      : isDragActive
                        ? '#DDEAF8' // Light red when rejected
                        : '#EFF5FC', // Default background
                '&:hover': !isBrowseDisabled && {
                  backgroundColor: '#DDEAF8', // Light blue on hover
                  borderColor: '#CBDDF6'
                }
              }}>
              <input {...getInputProps()} data-testid="file-input" />
              {loading && (
                <>
                  <CircularProgress sx={{ color: '#005CBC', fontSize: '36px' }} />
                  <Typography variant="extraSmall">{currentFile?.name}</Typography>
                </>
              )}
              {!loading && (
                <>
                  {mode === 'single' && uploadedFiles.length >= 1 ? (
                    <>
                      <SvgIcon
                        component={ExcelIcon}
                        inheritViewBox
                        sx={{
                          marginBottom: '8px',
                          width: '36px',
                          height: '36px'
                        }}
                      />
                      <Typography variant="extraSmall" sx={{ color: '#A0A0A0' }}>
                        {currentFile?.name}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <SvgIcon
                        component={ExcelIcon}
                        inheritViewBox
                        sx={{
                          marginBottom: '8px',
                          width: '36px',
                          height: '36px'
                        }}
                      />
                      <Typography variant="extraSmall">
                        {isDragActive ? 'Drop the files here ...' : 'Drag and drop your files here'}
                      </Typography>
                      <Typography variant="extraSmall">(Only .xls and .xlsx)</Typography>
                    </>
                  )}
                </>
              )}
            </Box>

            {uploadedFiles.length > 0 && !uploading && (
              <Box
                data-testid="uploaded-files-list"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  width: '100%'
                }}>
                {uploadedFiles.map((file, index) => (
                  <Stack
                    key={`${index + 1}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    data-testid={`file-item-${index}`}
                    sx={{
                      backgroundColor: '#EFF5FC',
                      padding: '8px',
                      borderRadius: '8px'
                    }}>
                    <Stack direction="row" alignItems="center" gap="8px">
                      <InsertDriveFileOutlinedIcon
                        sx={{
                          color: '#0078A7',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <Typography variant="extraSmall">{file.name}</Typography>
                    </Stack>
                    <DeleteOutlineOutlinedIcon
                      data-testid={`delete-file-${index}`}
                      sx={{
                        color: '#005CBC',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleDeleteFile(file.name)}
                    />
                  </Stack>
                ))}
              </Box>
            )}

            {uploading && (
              <Box
                data-testid="upload-progress"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  width: '100%'
                }}>
                <Typography variant="extraSmall" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Progress
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                  <Typography
                    variant="extraSmall"
                    color="text.secondary">{`${uploadProgress}%`}</Typography>
                  {/* <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{
                        width: '100%',
                        color: '#005CBC',
                        borderRadius: '8px',
                        height: '8px'
                      }}
                    /> */}
                  <ProgressBar
                    data-testid="progress-bar"
                    variant="determinate"
                    value={uploadProgress}
                    style={{
                      width: '100%',
                      color: '#005CBC',
                      borderRadius: '8px',
                      height: '8px'
                    }}
                  />
                </Box>
              </Box>
            )}

            <Button
              variant="outlined"
              component="label"
              size="small"
              data-testid="browse-file-test"
              endIcon={<UploadOutlinedIcon />}
              disabled={isBrowseDisabled}
              sx={{ cursor: isBrowseDisabled ? 'not-allowed' : 'pointer' }}
              fullWidth>
              Browse File
              {!isBrowseDisabled && (
                <input
                  data-testid="browse-input"
                  type="file"
                  hidden
                  accept=".xlsx, .xls"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
              )}
            </Button>
            {/* <Button
                variant="secondary"
                component="label"
                size="large"
                data-testid="browse-file-test"
                endIcon={<UploadOutlinedIcon />}
                disabled={isBrowseDisabled}
                sx={{ cursor: isBrowseDisabled ? 'not-allowed' : 'pointer' }}
                fullWidth>
                Browse File
                {!isBrowseDisabled && (
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={(e) => handleFileUpload(e.target.files)}
                  />
                )}
              </Button> */}
            <Button
              data-testid="upload-click"
              variant="contained"
              color="primary"
              size="small"
              onClick={handleUploadClick}
              // disabled={uploading}
              fullWidth
              disabled={uploading || !uploadedFiles.length}>
              Upload
            </Button>
          </Box>
        </Box>
        {/* </Box> */}
      </Box>
    </Modal>
  );
};

export default UploadModal;
